import { useCartDrawer } from '@iheartjane/cart';
import {
  Button,
  ChevronLeftIcon,
  DismissIcon,
  Flex,
  Link,
  Typography,
  useDesktopMediaQuery,
  useTabletMediaQuery,
} from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import { clsx } from 'clsx';
import { useLocation, useNavigate } from 'react-router';

import { useCheckout } from '@jane/shared-ecomm/providers';
import { useJaneUser } from '@jane/shared/data-access';

import styles from '../checkout.module.css';

interface CheckoutHeaderProps {
  closeButtonOnCheckout?: boolean;
  handleShowLogin: () => void;
  onClose?: () => void;
}

export const CheckoutHeader = ({
  handleShowLogin,
  closeButtonOnCheckout = false,
  onClose,
}: CheckoutHeaderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: janeUserData } = useJaneUser();
  const isMobile = useTabletMediaQuery({ width: 'max' });
  const isDesktop = useDesktopMediaQuery({ size: 'sm', width: 'min' });
  const {
    appInfo: { appMode, bloomUserExperience, disableAuthFeatures },
    cart,
  } = useCheckout();
  const { open: openCart } = useCartDrawer();

  const shouldUseNavigateBack = appMode === 'headless' || appMode === 'bloom';

  const signedIn = Boolean(janeUserData?.user?.id);
  const showLogin = !signedIn && !isMobile && !disableAuthFeatures;
  const cartItems = cart.products.reduce((acc, curr) => (acc += curr.count), 0);

  return (
    <header
      className={clsx(styles['checkoutHeader'], {
        [styles['checkoutHeader_bloom']]: bloomUserExperience,
      })}
    >
      <Flex alignItems="center" ml={isDesktop ? 24 : 16}>
        {shouldUseNavigateBack ? (
          <Link
            className={styles['backButton']}
            variant="minimal"
            onClick={() => navigate(-1)}
          >
            <ChevronLeftIcon altText="Go back" size="md" />
          </Link>
        ) : (
          <Link
            className={styles['backButton']}
            variant="minimal"
            to={location.state?.[0]?.url || `/stores/${cart.store?.id}`}
          >
            <ChevronLeftIcon altText="Go to homepage" size="md" />
          </Link>
        )}
      </Flex>
      <Flex alignItems="center" justifyContent="center">
        <Typography
          variant={
            isDesktop && !bloomUserExperience ? 'h2-semibold' : 'body-semibold'
          }
          as={!bloomUserExperience ? 'h1' : undefined}
          color="grays-black"
          branded
        >
          Checkout
        </Typography>
        {(isDesktop || bloomUserExperience) && (
          <>
            &nbsp;
            <Link
              onClick={() => openCart()}
              typography={
                isDesktop && !bloomUserExperience
                  ? 'h2-semibold'
                  : 'body-semibold'
              }
              color="grays-black"
              branded
            >
              (
              {pluralize({
                noun: 'item',
                number: cartItems,
              })}
              )
            </Link>
          </>
        )}
      </Flex>

      {showLogin && (
        <Button
          ml="auto"
          my="auto"
          size={isDesktop ? 'default' : 'small'}
          mr={isDesktop ? 64 : 16}
          label="Sign in"
          variant={isDesktop ? 'secondary' : 'minimal'}
          onClick={handleShowLogin}
          part="sign-in"
        />
      )}

      {closeButtonOnCheckout && (
        <Flex alignItems="center">
          <Link
            style={{ marginLeft: '80%' }}
            variant="minimal"
            onClick={onClose}
          >
            <DismissIcon altText="Close" size="md" />
          </Link>
        </Flex>
      )}
    </header>
  );
};
